import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { authGuard } from './guards/auth.guard'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/HomePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/pages/ReportsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('@/pages/CallbackPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/sites',
    name: 'Sites',
    component: () => import('@/pages/SitesPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/adhoc-reports',
    name: 'AdhocReports',
    component: () => import('@/pages/AdhocReportsPage.vue'),
    meta: { requiresAuth: true, title: 'Adhoc Reports' },
  },
  {
    path: '/surveys/:id/reports',
    name: 'SurveyReports',
    component: () => import('@/pages/ReportsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/surveys/:surveyId/bom-report',
    name: 'BomReport',
    component: () => import('@/pages/BomReportView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/surveys/:surveyId/appendices',
    name: 'AppendicesReport',
    component: () => import('@/pages/AppendicesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Route for blank/adhoc proposals that have no survey association
    path: '/proposals/:reportId/appendices',
    name: 'BlankProposalAppendices',
    component: () => import('@/pages/AppendicesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/surveys/:surveyId/cable-matrix',
    name: 'CableMatrixReport',
    component: () => import('@/pages/CableMatrixReportPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users',
    name: 'AdminUserRoles',
    component: () => import('@/features/admin/UserRoleManagementView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'platform_admin'],
      title: 'User Role Management',
    },
  },
  {
    path: '/admin/notification-subscriptions',
    name: 'NotificationSubscriptions',
    component: () => import('@/features/admin/NotificationSubscriptionsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'platform_admin'],
      title: 'Notification Subscriptions',
    },
  },
  {
    path: '/admin/organisations',
    name: 'AdminOrganisations',
    component: () => import('@/pages/OrganisationsAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'Organisations',
    },
  },
  {
    path: '/admin/organisations/:id',
    name: 'AdminOrganisationDetail',
    component: () => import('@/pages/OrganisationDetailPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'Organisation Detail',
    },
  },
  {
    path: '/admin/taxonomy',
    name: 'AdminTaxonomy',
    component: () => import('@/pages/TaxonomyAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'Taxonomy',
    },
  },
  {
    path: '/admin/hardware-vocabulary',
    name: 'AdminHardwareVocabulary',
    component: () => import('@/pages/HardwareVocabularyPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['manage-hardware', 'platform_admin'],
      title: 'Hardware Vocabulary',
    },
  },
  {
    // Consolidated role-aware Billing & Usage page (US #1035 / Task #1144) — replaces the six
    // routes below, which now just redirect here with the matching `?tab=`. `roles` is the
    // UNION of every tab's own role gate (see BillingPage.vue) so the guard admits anyone who
    // can see at least one tab; each tab's `visible` computed re-derives the exact same gate
    // its former standalone route had, so no access is widened by consolidating the routes.
    path: '/billing',
    name: 'Billing',
    component: () => import('@/pages/BillingPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['finance', 'organisation-admin', 'platform_finance', 'platform_admin'],
      title: 'Billing & Usage',
    },
  },
  {
    // Redirects (Task #1144) — kept so existing bookmarks/links never 404. The auth guard
    // evaluates the FINAL resolved route (`/billing`, above), not this record's meta.
    path: '/admin/ai-costs',
    redirect: () => ({ path: '/billing', query: { tab: 'usage' } }),
  },
  {
    path: '/admin/billing',
    redirect: () => ({ path: '/billing', query: { tab: 'rates-fx' } }),
  },
  {
    path: '/admin/invoices',
    redirect: () => ({ path: '/billing', query: { tab: 'all-invoices' } }),
  },
  {
    path: '/admin/ai-spend-caps',
    redirect: () => ({ path: '/billing', query: { tab: 'caps' } }),
  },
  {
    path: '/billing/invoices',
    redirect: () => ({ path: '/billing', query: { tab: 'my-invoices' } }),
  },
  {
    path: '/billing/details',
    redirect: () => ({ path: '/billing', query: { tab: 'billing-details' } }),
  },
  {
    path: '/admin/brand-standards',
    name: 'AdminBrandStandards',
    component: () => import('@/pages/BrandStandardsSettingsPage.vue'),
    meta: {
      requiresAuth: true,
      // organisation-admin included so the UI route matches the backend requireAnyRole
      // admin bypass (brand standards are org-scoped data) — #909.
      roles: ['manage-brand-standards', 'organisation-admin', 'platform_admin'],
      title: 'Brand Standards',
    },
  },
  {
    path: '/documents',
    name: 'DocumentsManagement',
    component: () => import('@/pages/DocumentsManagementPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'appendix-report-signoff', 'documents-manager'],
      title: 'Documents Management',
    },
  },
  {
    path: '/reports/:reportId/documents',
    name: 'ProposalDocuments',
    component: () => import('@/pages/ProposalDocumentsAdminPage.vue'),
    meta: {
      requiresAuth: true,
      // Had no `roles` at all, so any authenticated user could reach it — and since #1367 this
      // page renders the same DocumentsList/DocumentsTable with inline edit/delete affordances.
      // Matches the sibling /documents route so read access is consistent between the two
      // (#1367 review). The mutation gate is useCanManageDocuments(), mirroring the endpoint.
      roles: ['organisation-admin', 'appendix-report-signoff', 'documents-manager'],
      title: 'Proposal Documents',
    },
  },
  {
    path: '/admin/proposal-settings',
    name: 'ProposalSettings',
    component: () => import('@/pages/ProposalSettingsAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'platform_admin'],
      title: 'Proposal Settings',
    },
  },
  {
    path: '/admin/ai-settings',
    name: 'AiModelSettings',
    component: () => import('@/pages/AiModelSettingsPage.vue'),
    meta: {
      // #820/#826: ai_model_configs is a GLOBAL table shared by all tenants, so AI Settings
      // is platform-admin only — an org admin must not read/repoint every tenant's models.
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'AI Settings',
    },
  },
  {
    path: '/builder',
    name: 'SurveyBuilder',
    component: () => import('@/pages/SurveyBuilderAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-builder', 'survey-admin'],
      title: 'Survey Builder',
    },
  },
  {
    path: '/builder/:id',
    name: 'SurveyBuilderDetail',
    component: () => import('@/pages/SurveyBuilderDetailPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-builder', 'survey-admin'],
      title: 'Survey Detail',
    },
  },
  {
    path: '/builder/:id/hardware',
    name: 'SurveyHardwareManagement',
    component: () => import('@/pages/HardwareManagementPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-builder', 'survey-admin'],
      title: 'Manage Hardware Categories',
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: {
      requiresAuth: true,
      roles: [
        'platform_admin',
        'organisation-admin',
        'appendix-report-signoff',
        'documents-manager',
        'survey-builder',
        'survey-admin',
        'manage-hardware',
        'manage-brand-standards',
      ],
      title: 'Settings',
    },
  },
  {
    path: '/settings/manage-surveys',
    name: 'ManageSurveys',
    component: () => import('@/pages/ManageSurveysPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-admin'],
      title: 'Manage Surveys',
    },
  },
  {
    path: '/settings/manage-devices',
    name: 'ManageDevices',
    component: () => import('@/pages/ManageDevicesPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-admin', 'survey-builder'],
      title: 'Manage My Devices',
    },
  },
  {
    path: '/public/proposals/:token',
    name: 'PublicProposal',
    component: () => import('@/pages/public/PublicProposalView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Proposal Review',
    },
  },
  {
    path: '/invite/:token',
    name: 'InviteAccept',
    component: () => import('@/pages/InviteAcceptPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Accept Invitation',
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Note: Auth guard is registered in main.ts after Pinia is initialized
// to avoid "getActivePinia()" errors

export default router

export { authGuard }
